import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

function CCDialog({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({ name: '', acumatica_code: '', description: '' });
  useEffect(() => { if (open) setForm(initial || { name: '', acumatica_code: '', description: '' }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Add'} Cost Center</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e=>set('name',e.target.value)} className="mt-1 h-8 text-sm" data-testid="cc-name-input" /></div>
          <div><Label className="text-xs">Acumatica Code *</Label><Input value={form.acumatica_code} onChange={e=>set('acumatica_code',e.target.value)} className="mt-1 h-8 text-sm font-mono" placeholder="e.g. IT-DEPT" data-testid="cc-code-input" /></div>
          <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e=>set('description',e.target.value)} className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)} data-testid="cc-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CostCentersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await API.get('/cost-centers'); setItems(r.data); }
    catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) { await API.put(`/cost-centers/${editing.id}`, form); toast.success('Updated'); }
      else { await API.post('/cost-centers', form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/cost-centers/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Cost Centers</h1>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-cc-btn">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      <p className="text-sm text-slate-500">Map cost centers to Acumatica codes for export.</p>
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="cost-centers-table">
          <thead><tr>
            <th>Cost Center</th><th>Acumatica Code</th><th>Description</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center py-8 text-slate-400"><RefreshCw size={16} className="animate-spin inline" /></td></tr>
            ) : items.map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td className="font-mono text-sm text-slate-600">{c.acumatica_code}</td>
                <td className="text-slate-500 text-sm">{c.description || '—'}</td>
                <td className="text-right">
                  <button onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1 text-slate-400 hover:text-emerald-600 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => setDeleteId(c.id)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CCDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Cost Center?</AlertDialogTitle><AlertDialogDescription>This may affect existing allocations.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
